import { Code2 } from 'lucide-react';
import type { FC, SVGProps } from 'react';
import { Images } from '@/assets/images';
import {
  PLATFORM_REGISTRY,
  type PlatformId,
} from '@kirin/api-client/contracts/platform-registry';

/**
 * Icon component type that works with both SVG icons and Lucide icons
 */
export type IconComponent = FC<SVGProps<SVGSVGElement>>;

/**
 * Known platform types for IDE clients.
 * Derived from the platform registry.
 */
export type Platform = PlatformId;

/**
 * Map from PlatformId to its icon component.
 * Every registry entry must have an icon here; the type enforces completeness.
 */
const PLATFORM_ICONS: Record<PlatformId, IconComponent> = {
  cursor: Images.CursorIcon as IconComponent,
  claude: Images.ClaudeIcon as IconComponent,
  claude_desktop: Images.ClaudeDesktopIcon as IconComponent,
  // KIR-3908: Cowork-hook provenance marker — reuse the Claude Desktop glyph.
  claude_desktop_hook: Images.ClaudeDesktopIcon as IconComponent,
  copilot: Images.CopilotIcon as IconComponent,
  vscode: Images.VSCodeIcon as IconComponent,
  windsurf: Images.WindsurfIcon as IconComponent,
  // KIR-2217: no branded Antigravity SVG exists yet. Map to lucide `Code2`
  // (same glyph `getPlatformIcon` uses for unknown/unreleased platforms)
  // rather than inventing a stand-in that resembles another product's mark.
  // When design ships the real brand SVG, drop it in `assets/images/`,
  // import here as `Images.AntigravityIcon`, and swap this line.
  antigravity: Code2 as unknown as IconComponent,
  gemini_cli: Images.GeminiCliIcon as IconComponent,
  // KIR-3334: branded Codex app icon (converted from the ticket's app.icns to
  // assets/images/codex.png, wrapped by CodexIcon).
  codex: Images.CodexIcon as IconComponent,
};

/**
 * Display labels for platforms — always the registry `displayName`. Visibility
 * gating (whether a platform's data surfaces at all for a given org) is handled
 * upstream; once data reaches a chip, the label must be the canonical product
 * name regardless of feature-flag state.
 */
export const platformLabels: Record<PlatformId, string> = Object.fromEntries(
  Object.entries(PLATFORM_REGISTRY).map(([id, meta]) => [id, meta.displayName])
) as Record<PlatformId, string>;

const KIRIN_CLIENT_IDS = new Set<string>(Object.keys(platformLabels));

/**
 * Get the display label for a platform. Known platforms use their registry
 * display name; unknown ids get a capitalised fallback.
 */
export function getPlatformLabel(platform: string): string {
  const normalized = platform.toLowerCase();
  if (normalized in platformLabels) {
    return platformLabels[normalized as PlatformId];
  }
  return platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase();
}

/**
 * Label for a client row: Kirin client IDs (cursor, claude, …) always use the
 * canonical product name. Shadow AI may send a looser display name (e.g. "Claude")
 * that must not override the Kirin mapping for those IDs.
 */
export function getIdeClientDisplayLabel(
  client: string,
  displayName?: string | null
): string {
  const normalized = client.toLowerCase();
  if (KIRIN_CLIENT_IDS.has(normalized)) {
    return getPlatformLabel(normalized);
  }
  return displayName ?? getPlatformLabel(client);
}

/**
 * Get the appropriate icon for a platform. Any platform in the registry
 * returns its branded icon (antigravity maps to `Code2` until a branded SVG
 * ships). Unknown ids fall back to `Code2`.
 */
export function getPlatformIcon(platform: string): IconComponent {
  const normalized = platform.toLowerCase();
  if (!(normalized in PLATFORM_REGISTRY)) {
    return Code2 as unknown as IconComponent;
  }
  return PLATFORM_ICONS[normalized as PlatformId];
}
